/**
 * Pure state for the blade stack: the ordered list of open right-side panels. The
 * newest entry is on top; opening a kind that is already open with the same id
 * replaces it (so re-selecting a record swaps the detail rather than stacking a
 * duplicate) and bumps its instance, so the panel mounts afresh with the new params;
 * closing an entry also closes everything above it.
 */

export type BladeSize = "sm" | "md" | "lg" | "xl"

/** Panel widths, matching the Timesheet Hub's filter drawer (392) and detail slide-out (680) */
export const BLADE_WIDTHS: Record<BladeSize, string> = {
  sm: "392px",
  md: "560px",
  lg: "680px",
  xl: "min(1100px, 92vw)",
}

/** How far each blade beneath the top one peeks out on the left, in px */
export const BLADE_STACK_OFFSET = 24

export interface BladeEntry<TParams = unknown> {
  /** Stable identity; the same id re-opened replaces the existing entry */
  id: string
  /** Registry key that decides which component renders the entry */
  kind: string
  params: TParams
  size: BladeSize
  /** Short label shown in the trail of blades opened above this one */
  label: string
  /** Incremented each time the same id is re-opened; part of the render key */
  instance: number
}

export type BladeAction =
  | { type: "open"; entry: Omit<BladeEntry, "instance"> }
  | { type: "close"; id: string }
  | { type: "popTo"; index: number }
  | { type: "closeAll" }

export function bladeStackReducer(stack: BladeEntry[], action: BladeAction): BladeEntry[] {
  switch (action.type) {
    case "open": {
      const index = stack.findIndex((entry) => entry.id === action.entry.id)
      // Re-opening an entry that is already in the stack brings the stack back to it, as a new instance
      if (index === -1) return [...stack, { ...action.entry, instance: 0 }]
      return [...stack.slice(0, index), { ...action.entry, instance: stack[index].instance + 1 }]
    }
    case "close": {
      const index = stack.findIndex((entry) => entry.id === action.id)
      return index === -1 ? stack : stack.slice(0, index)
    }
    case "popTo":
      return stack.slice(0, Math.max(0, action.index + 1))
    case "closeAll":
      return stack.length === 0 ? stack : []
  }
}

/** The labels of every blade beneath `index`, oldest first — the header trail */
export function bladeTrail(stack: BladeEntry[], index: number): string[] {
  return stack.slice(0, index).map((entry) => entry.label)
}

/** The React key of an entry: a re-opened blade is a new instance and remounts */
export function bladeKey(entry: BladeEntry): string {
  return `${entry.id}#${entry.instance}`
}
